<template>
  <div id="dashboard">
    <Navbar current="Dashboard" />
    <Sidebar />
    <Container col="col-12">
      <Header
        icon="fa-solid fa-chart-line"
        title="Dashboard"
        :tabs="tabs"
        @tabClicked="handleTabClicked"
      />

      <!-- Botão forçar atualização -->
      <div class="d-flex justify-content-end mb-3">
        <button
          class="btn btn-outline-secondary btn-sm"
          @click="forceRefreshCurrentTab"
          :disabled="loadingGeneral || loadingUsage || loadingChurn || loadingFinances || loadingContent"
        >
          <i class="fa-solid fa-rotate-right me-1"></i>
          Forçar atualização
        </button>
      </div>

      <!-- Aba Principal -->
      <div v-if="activeTab === 'principal'">
        <LoadingContainer v-if="loadingGeneral" />
        <div v-else-if="generalData">
          <!-- SEÇÃO 1: Cards Big Numbers - Totais por tipo profissional -->
          <h2 class="text-paragraph mb-3">Totais de usuários por tipo de profissional</h2>
          <div class="row mb-4">
            <div class="col-12 col-lg">
              <div class="card">
                <div class="card-body text-center">
                  <h5 class="text-muted mb-2">
                    <i class="fa-solid fa-user-doctor me-2"></i>Médicos
                  </h5>
                  <div class="display-4">{{ generalData.totals.doctor || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg">
              <div class="card">
                <div class="card-body text-center">
                  <h5 class="text-muted mb-2">
                    <i class="fa-solid fa-graduation-cap me-2"></i>Estudantes
                  </h5>
                  <div class="display-4">{{ generalData.totals.student || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg">
              <div class="card">
                <div class="card-body text-center">
                  <h5 class="text-muted mb-2">
                    <i class="fa-solid fa-tooth me-2"></i>Dentistas
                  </h5>
                  <div class="display-4">{{ generalData.totals.dentist || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg">
              <div class="card">
                <div class="card-body text-center">
                  <h5 class="text-muted mb-2">
                    <i class="fa-solid fa-user me-2"></i>Outros
                  </h5>
                  <div class="display-4">{{ generalData.totals.other || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg">
              <div class="card">
                <div class="card-body text-center">
                  <h5 class="text-muted mb-2">
                    <i class="fa-solid fa-handshake me-2"></i>Parceiros
                  </h5>
                  <div class="display-4">{{ generalData.totals.partner || 0 }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- SEÇÃO 2: Gráfico de barras - Cadastros mensais -->
          <h2 class="text-paragraph mb-3">Cadastros mensais</h2>
          <div class="card mb-4">
            <div class="card-body" style="height: 300px;">
              <BarChart v-if="monthlySignupsChart.labels.length" :chartData="monthlySignupsChart" />
            </div>
          </div>

          <!-- SEÇÃO 3: Gráfico de barras agrupadas - Por tipo profissional -->
          <h2 class="text-paragraph mb-3">Cadastros por tipo profissional</h2>
          <div class="card mb-4">
            <div class="card-body" style="height: 300px;">
              <GroupedBarChart v-if="professionalTypeChart.labels.length" :chartData="professionalTypeChart" />
            </div>
          </div>

          <!-- SEÇÃO 4, 5 e 6: Assinantes em uma linha -->
          <div class="row mb-4">
            <!-- Total assinantes -->
            <div class="col-12 col-lg-2">
              <h2 class="text-paragraph mb-3">Total de assinantes</h2>
              <div class="card h-100">
                <div class="card-body text-center d-flex flex-column justify-content-center">
                  <div class="display-2">{{ generalData.subscriber_count || 0 }}</div>
                  <p class="text-muted mb-0">assinantes ativos</p>
                </div>
              </div>
            </div>

            <!-- Gráfico de pizza - Tipos de assinantes -->
            <div class="col-12 col-lg-4">
              <h2 class="text-paragraph mb-3">Tipos de assinantes</h2>
              <div class="card h-100">
                <div class="card-body" style="height: 250px;">
                  <PieChart v-if="subscribersTypeChart.labels.length" :chartData="subscribersTypeChart" />
                </div>
              </div>
            </div>

            <!-- Gráfico de barras - Assinaturas por gateway -->
            <div class="col-12 col-lg-6">
              <h2 class="text-paragraph mb-3">Assinaturas mensais por gateway</h2>
              <div class="card h-100">
                <div class="card-body" style="height: 250px;">
                  <StackedBarChart v-if="gatewayChart.labels.length" :chartData="gatewayChart" />
                </div>
              </div>
            </div>
          </div>

          <!-- SEÇÃO 7: Conversão por Coorte -->
          <h2 class="text-paragraph mb-3 mt-5">Conversão por Coorte (Mensal)</h2>
          <p class="text-muted small mb-3">Agrupa usuários pelo mês de cadastro e mostra quantos viraram assinantes pagos em qualquer momento futuro.</p>
          <div class="card mb-4">
            <div class="card-body" style="height: 300px;">
              <ConversionChart
                v-if="cohortConversionChart.labels.length"
                :chartData="cohortConversionChart"
                registrationsLabel="Cadastros"
                subscribersLabel="Assinantes"
              />
            </div>
          </div>

          <!-- SEÇÃO 8: Conversão Mensal (Fluxo) -->
          <h2 class="text-paragraph mb-3">Conversão Mensal (Fluxo)</h2>
          <p class="text-muted small mb-3">Mostra o fluxo mensal de cadastros vs novas assinaturas (sem relação entre eles). A taxa de conversão compara novas assinaturas com cadastros do mesmo mês.</p>
          <div class="card mb-4">
            <div class="card-body" style="height: 300px;">
              <ConversionChart
                v-if="monthlyConversionFlowChart.labels.length"
                :chartData="monthlyConversionFlowChart"
                registrationsLabel="Cadastros"
                subscribersLabel="Novas Assinaturas"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Aba Uso -->
      <div v-if="activeTab === 'uso'">
        <LoadingContainer v-if="loadingUsage" />
        <div v-else-if="usageData">

          <!-- ========== SEÇÃO 1: VISÃO GERAL ========== -->
          <div class="border-bottom pb-2 mb-4">
            <h4 class="mb-0"><i class="fa-solid fa-chart-pie me-2"></i>Visão Geral</h4>
          </div>
          <div class="row mb-5">
            <div class="col-12 col-lg-3 mb-3">
              <div class="card">
                <div class="card-body text-center">
                  <h5 class="text-muted mb-2">
                    <i class="fa-solid fa-prescription me-2"></i>Receitas
                  </h5>
                  <div class="display-4">{{ usageData.totals.recipes || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg-3 mb-3">
              <div class="card">
                <div class="card-body text-center">
                  <h5 class="text-muted mb-2">
                    <i class="fa-solid fa-hospital-user me-2"></i>Pacientes
                  </h5>
                  <div class="display-4">{{ usageData.totals.patients || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg-3 mb-3">
              <div class="card">
                <div class="card-body text-center">
                  <h5 class="text-muted mb-2">
                    <i class="fa-solid fa-notes-medical me-2"></i>Prontuários
                  </h5>
                  <div class="display-4">{{ usageData.totals.medical_records || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg-3 mb-3">
              <div class="card">
                <div class="card-body text-center">
                  <h5 class="text-muted mb-2">
                    <i class="fa-solid fa-star me-2"></i>Favoritos
                  </h5>
                  <div class="display-4">{{ usageData.totals.favorited_contents || 0 }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- ========== SEÇÃO 2: RECEITAS ========== -->
          <div class="border-bottom pb-2 mb-4">
            <h4 class="mb-0"><i class="fa-solid fa-prescription me-2"></i>Receitas</h4>
          </div>
          <div class="row mb-4">
            <div class="col-12 col-lg-4 mb-3">
              <p class="text-muted small mb-2">Tipos de receita emitidas</p>
              <div class="card">
                <div class="card-body" style="height: 280px; position: relative;">
                  <PieChart v-if="recipeTypeChart.labels.length" :chartData="recipeTypeChart" />
                </div>
              </div>
            </div>
            <div class="col-12 col-lg-8 mb-3">
              <p class="text-muted small mb-2">Emissão de receitas mensal</p>
              <div class="card">
                <div class="card-body" style="height: 280px; position: relative;">
                  <BarChart v-if="monthlyRecipesChart.labels.length" :chartData="monthlyRecipesChart" />
                </div>
              </div>
            </div>
          </div>
          <div class="row mb-5">
            <div class="col-12 col-lg-4 mb-3">
              <p class="text-muted small mb-2">Receitas por tipo de assinatura digital</p>
              <div class="card">
                <div class="card-body" style="height: 280px; position: relative;">
                  <PieChart v-if="recipeDigitalTypeChart.labels.length" :chartData="recipeDigitalTypeChart" />
                </div>
              </div>
            </div>
            <div class="col-12 col-lg-8 mb-3">
              <p class="text-muted small mb-2">Emissão mensal por tipo de assinatura</p>
              <div class="card">
                <div class="card-body" style="height: 280px; position: relative;">
                  <StackedBarChart v-if="monthlyRecipesByDigitalTypeChart.labels.length" :chartData="monthlyRecipesByDigitalTypeChart" />
                </div>
              </div>
            </div>
          </div>

          <!-- ========== SEÇÃO 3: PRONTUÁRIOS ========== -->
          <div class="border-bottom pb-2 mb-4">
            <h4 class="mb-0"><i class="fa-solid fa-notes-medical me-2"></i>Prontuários</h4>
          </div>
          <div class="row mb-5">
            <div class="col-12 col-lg-4 mb-3">
              <p class="text-muted small mb-2">Tipos de prontuário</p>
              <div class="card">
                <div class="card-body" style="height: 280px; position: relative;">
                  <PieChart v-if="medicalRecordTypeChart.labels.length" :chartData="medicalRecordTypeChart" />
                </div>
              </div>
            </div>
            <div class="col-12 col-lg-8 mb-3">
              <p class="text-muted small mb-2">Emissão de prontuários mensal</p>
              <div class="card">
                <div class="card-body" style="height: 280px; position: relative;">
                  <BarChart v-if="monthlyMedicalRecordsChart.labels.length" :chartData="monthlyMedicalRecordsChart" />
                </div>
              </div>
            </div>
          </div>

          <!-- ========== SEÇÃO 4: MÉDICOS ASSINANTES ========== -->
          <div class="border-bottom pb-2 mb-4">
            <h4 class="mb-0"><i class="fa-solid fa-user-doctor me-2"></i>Médicos Assinantes</h4>
          </div>
          <div class="row mb-4">
            <div class="col-12 col-lg-2 mb-3">
              <p class="text-muted small mb-2">Total</p>
              <div class="card">
                <div class="card-body text-center d-flex flex-column justify-content-center" style="height: 250px;">
                  <div class="display-2">{{ usageData.doctor_subscriber_usage_type?.total_medicos_pagantes || 0 }}</div>
                  <p class="text-muted mb-0">médicos</p>
                </div>
              </div>
            </div>
            <div class="col-12 col-lg-5 mb-3">
              <p class="text-muted small mb-2">Engajamento - Quantas features cada médico utiliza</p>
              <div class="card">
                <div class="card-body" style="height: 250px; position: relative;">
                  <PieChart
                    v-if="doctorEngagementChart.labels.length"
                    :chartData="doctorEngagementChart"
                  />
                </div>
              </div>
            </div>
            <div class="col-12 col-lg-5 mb-3">
              <p class="text-muted small mb-2">Adoção de Features - Médicos que usam cada feature</p>
              <div class="card">
                <div class="card-body" style="height: 250px; position: relative;">
                  <HorizontalBarChart
                    v-if="featureAdoptionChart.labels.length"
                    :chartData="featureAdoptionChart"
                    :showPercentage="true"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="row mb-5">
            <div class="col-12">
              <p class="text-muted small mb-2">Detalhamento de Combinações - Distribuição das combinações de features</p>
              <div class="card">
                <div class="card-body" style="height: 400px; position: relative;">
                  <PieChart v-if="doctorUsageChart.labels.length" :chartData="doctorUsageChart" />
                </div>
              </div>
            </div>
          </div>

          <!-- ========== SEÇÃO 5: CONTEÚDOS ========== -->
          <div class="border-bottom pb-2 mb-4">
            <h4 class="mb-0"><i class="fa-solid fa-book-open me-2"></i>Conteúdos</h4>
          </div>
          <div class="row mb-5" v-if="contentViewsByUserTypeChart.labels.length">
            <div class="col-12 col-lg-6 mb-3">
              <p class="text-muted small mb-2">Uso de conteúdos por tipo de usuário</p>
              <div class="card">
                <div class="card-body" style="height: 280px; position: relative;">
                  <PieChart :chartData="contentViewsByUserTypeChart" />
                </div>
              </div>
            </div>
          </div>

          <!-- ========== SEÇÃO 6: CHAT IA ========== -->
          <div class="border-bottom pb-2 mb-4">
            <h4 class="mb-0"><i class="fa-solid fa-robot me-2"></i>Chat IA</h4>
          </div>
          <div class="row mb-4">
            <div class="col-12">
              <p class="text-muted small mb-2">Mensagens mensais enviadas por usuários</p>
              <div class="card">
                <div class="card-body" style="height: 300px; position: relative;">
                  <BarChart v-if="monthlyAiChatMessagesChart.labels.length" :chartData="monthlyAiChatMessagesChart" />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Aba Churn -->
      <div v-if="activeTab === 'churn'">
        <LoadingContainer v-if="loadingChurn" />
        <div v-else-if="churnData">
          <!-- SEÇÃO 1: Churn rate mensal Stripe -->
          <h2 class="text-paragraph mb-3">Churn rate mensal Stripe (%)</h2>
          <p class="text-muted small mb-3">Porcentagem de cancelamentos frente à base ativa de assinantes Stripe. A base ativa representa o total de assinantes ativos no primeiro dia do mês. Também exibe novas assinaturas e cancelamentos no período.</p>
          <div class="card mb-4">
            <div class="card-body" style="height: 300px; position: relative;">
              <ChurnLineChart v-if="stripeChurnChart.labels.length" :chartData="stripeChurnChart" />
            </div>
          </div>

          <!-- SEÇÃO 2: Churn rate mensal In-App Purchase -->
          <h2 class="text-paragraph mb-3">Churn rate mensal In-App Purchase (%)</h2>
          <p class="text-muted small mb-3">Porcentagem de cancelamentos frente à base ativa de assinantes In-App Purchase (App Store e Google Play). A base ativa representa o total de assinantes ativos no primeiro dia do mês. Também exibe novas assinaturas e cancelamentos no período.</p>
          <div class="card mb-4">
            <div class="card-body" style="height: 300px; position: relative;">
              <ChurnLineChart v-if="inappChurnChart.labels.length" :chartData="inappChurnChart" />
            </div>
          </div>

          <!-- SEÇÃO 3: Cohort de churn Stripe -->
          <h2 class="text-paragraph mb-3">Cohort de churn Stripe (tempo até cancelar)</h2>
          <p class="text-muted small mb-3">Mostra quanto tempo os usuários ficaram ativos antes de cancelar. Cada barra representa a quantidade de cancelamentos agrupados por meses de uso. Se a maioria cancela no 1º mês, indica problemas no onboarding; entre 1-2 meses, pode ser percepção de valor.</p>
          <div class="card mb-4">
            <div class="card-body" style="height: 280px; position: relative;">
              <BarChart v-if="churnCohortChart.labels.length" :chartData="churnCohortChart" />
            </div>
          </div>

          <!-- SEÇÃO 4: Cohort de churn In-App -->
          <h2 class="text-paragraph mb-3">Cohort de churn In-App Purchase (tempo até cancelar)</h2>
          <p class="text-muted small mb-3">Mostra quanto tempo os usuários de In-App Purchase ficaram ativos antes de cancelar. Cada barra representa a quantidade de cancelamentos agrupados por meses de uso.</p>
          <div class="card mb-4">
            <div class="card-body" style="height: 280px; position: relative;">
              <BarChart v-if="inappChurnCohortChart.labels.length" :chartData="inappChurnCohortChart" />
            </div>
          </div>
        </div>
      </div>

      <!-- Aba Financeiro -->
      <div v-if="activeTab === 'financeiro'">
        <LoadingContainer v-if="loadingFinances" />
        <div v-else-if="financesData">
          <!-- SEÇÃO 1: MRR Total -->
          <h2 class="text-paragraph mb-3">MRR Total (Stripe + In-App)</h2>
          <p class="text-muted small mb-3">Receita recorrente mensal combinada de todas as plataformas. Planos anuais têm o valor dividido por 12 para refletir o equivalente mensal.</p>
          <div class="card mb-4">
            <div class="card-body" style="height: 300px; position: relative;">
              <MrrLineChart v-if="totalMrrChart.labels.length" :chartData="totalMrrChart" color="#e76f51" />
            </div>
          </div>

          <!-- SEÇÃO 2: MRR Stripe -->
          <h2 class="text-paragraph mb-3">MRR Stripe</h2>
          <p class="text-muted small mb-3">Receita recorrente mensal das assinaturas via Stripe (cartão de crédito). Planos anuais têm o valor dividido por 12 para refletir o equivalente mensal.</p>
          <div class="card mb-4">
            <div class="card-body" style="height: 300px; position: relative;">
              <MrrLineChart v-if="stripeMrrChart.labels.length" :chartData="stripeMrrChart" color="#2e4664" />
            </div>
          </div>

          <!-- SEÇÃO 3: MRR In-App Purchase -->
          <h2 class="text-paragraph mb-3">MRR In-App Purchase</h2>
          <p class="text-muted small mb-3">Receita recorrente mensal das assinaturas via App Store e Google Play. Planos anuais têm o valor dividido por 12 para refletir o equivalente mensal.</p>
          <div class="card mb-4">
            <div class="card-body" style="height: 300px; position: relative;">
              <MrrLineChart v-if="inappMrrChart.labels.length" :chartData="inappMrrChart" color="#4a7c59" />
            </div>
          </div>

          <!-- SEÇÃO 4: Receita Total -->
          <h2 class="text-paragraph mb-3">Receita Total (Stripe + In-App)</h2>
          <p class="text-muted small mb-3">Receita efetivamente recebida no mês, somando todas as plataformas. Diferente do MRR, aqui os valores refletem o que foi cobrado no período, sem mensalização de planos anuais.</p>
          <div class="card mb-4">
            <div class="card-body" style="height: 300px; position: relative;">
              <MrrLineChart v-if="totalRevenueChart.labels.length" :chartData="totalRevenueChart" color="#e76f51" label="Receita" />
            </div>
          </div>

          <!-- SEÇÃO 5: Receita Stripe -->
          <h2 class="text-paragraph mb-3">Receita Stripe</h2>
          <p class="text-muted small mb-3">Receita efetivamente recebida via Stripe (cartão de crédito) no mês. Valores refletem o que foi cobrado no período, incluindo planos anuais pelo valor integral no mês da cobrança.</p>
          <div class="card mb-4">
            <div class="card-body" style="height: 300px; position: relative;">
              <MrrLineChart v-if="stripeRevenueChart.labels.length" :chartData="stripeRevenueChart" color="#2e4664" label="Receita" />
            </div>
          </div>

          <!-- SEÇÃO 6: Receita In-App Purchase -->
          <h2 class="text-paragraph mb-3">Receita In-App Purchase</h2>
          <p class="text-muted small mb-3">Receita efetivamente recebida via App Store e Google Play no mês. Valores refletem o que foi cobrado no período, incluindo planos anuais pelo valor integral no mês da cobrança.</p>
          <div class="card mb-4">
            <div class="card-body" style="height: 300px; position: relative;">
              <MrrLineChart v-if="inappRevenueChart.labels.length" :chartData="inappRevenueChart" color="#4a7c59" label="Receita" />
            </div>
          </div>
        </div>
      </div>

      <!-- Aba Conteúdo -->
      <div v-if="activeTab === 'conteudo'">
        <LoadingContainer v-if="loadingContent" />
        <div v-else-if="contentData">
          <!-- SEÇÃO 1: Top 30 Conteúdos mais vistos -->
          <h2 class="text-paragraph mb-3">Top 30 conteúdos mais vistos</h2>
          <p class="text-muted small mb-3">Ranking dos conteúdos com mais visualizações. <i class="fa-solid fa-star text-warning"></i> indica conteúdo exclusivo para assinantes.</p>
          <div class="card mb-4">
            <div class="card-body p-3">
              <div class="list-group">
                <router-link
                  v-for="(content, index) in topViewedContents"
                  :key="content.content_id"
                  :to="`/conteudos/detalhes/${content.content_id}`"
                  class="list-group-item list-group-item-action d-flex align-items-center py-3"
                >
                  <span
                    class="badge rounded-pill me-3 d-flex align-items-center justify-content-center"
                    :class="{
                      'bg-warning text-dark': index === 0,
                      'bg-secondary': index === 1,
                      'bg-danger': index === 2,
                      'bg-light text-muted border': index > 2
                    }"
                    style="width: 32px; height: 32px; font-size: 0.85rem;"
                  >
                    {{ index + 1 }}
                  </span>
                  <div class="flex-grow-1 me-3">
                    <div class="fw-medium">
                      <i v-if="content.only_subscribers" class="fa-solid fa-star text-warning me-1" title="Exclusivo para assinantes"></i>
                      {{ content.title }}
                    </div>
                  </div>
                  <span class="badge bg-primary rounded-pill px-3 py-2">
                    <i class="fa-solid fa-eye me-1"></i>
                    {{ content.views_count.toLocaleString('pt-BR') }}
                  </span>
                </router-link>
              </div>
            </div>
          </div>

          <!-- SEÇÃO 2: Visualizações diárias -->
          <h2 class="text-paragraph mb-3">Visualizações diárias (últimos 30 dias)</h2>
          <p class="text-muted small mb-3">Quantidade de visualizações de conteúdo por dia.</p>
          <div class="card mb-4">
            <div class="card-body" style="height: 300px; position: relative;">
              <BarChart v-if="dailyContentViewsChart.labels.length" :chartData="dailyContentViewsChart" />
            </div>
          </div>

          <!-- SEÇÃO 3: Visualizações mensais -->
          <h2 class="text-paragraph mb-3">Visualizações mensais (últimos 12 meses)</h2>
          <p class="text-muted small mb-3">Quantidade de visualizações de conteúdo por mês.</p>
          <div class="card mb-4">
            <div class="card-body" style="height: 300px; position: relative;">
              <BarChart v-if="monthlyContentViewsChart.labels.length" :chartData="monthlyContentViewsChart" />
            </div>
          </div>
        </div>
      </div>
    </Container>
  </div>
</template>

<script>
import DashboardController from "./DashboardController";
export default DashboardController;
</script>
